import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  BenefitsSection,
  FaqSection,
  FinalCtaSection,
  FooterSection,
  GoogleReviewsSection,
  HeroSection,
  HOMEPAGE_FAQS,
  PricingSection,
  ContrastSection,
  ProblemsSection,
  SecuritySection,
  TreatmentIntelligenceSection,
  TrustStrip,
  AuditTrailSection,
  WorkflowSection,
  V2Nav,
  useRevealOnScroll,
} from '../../components/homepage-v2';
import '../../components/homepage-v2/homepageV2.css';
import { WebsiteSchema, OrganizationSchema } from '../../components/SEO/StructuredData';
import { getSoftwareApplicationSchema } from '../../utils/softwareApplicationSchema';
import { usePageMeta } from '../../hooks/usePageMeta';
import DemoLeadPopup from '../../components/homepage-v2/DemoLeadPopup';

const PAGE_TITLE = 'AI-assisted dental consent for UK practices | SmileConsent';
const PAGE_DESCRIPTION =
  'AI-assisted consent drafting for UK dental practices. Select a patient, add treatments and costs, review and edit the draft, then send for digital signature. Your clinicians remain responsible for final wording and compliance.';
const CANONICAL_URL = 'https://smileconsent.co.uk/';
const COOKIE_KEY = 'smileConsent-cookie-consent';

function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(COOKIE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const choose = (value) => {
    try {
      localStorage.setItem(COOKIE_KEY, value);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="hpv2-cookie" role="dialog" aria-label="Cookie consent">
      <p>
        We use cookies to remember your preferences and understand how the site is used.
        See our <Link to="/privacy">privacy policy</Link> for details.
      </p>
      <div className="hpv2-cookie-actions">
        <button type="button" className="hpv2-btn hpv2-btn--ghost" onClick={() => choose('declined')}>
          Decline
        </button>
        <button type="button" className="hpv2-btn hpv2-btn--primary" onClick={() => choose('accepted')}>
          Accept
        </button>
      </div>
    </div>
  );
}

export default function HomepageV2() {
  const pageRef = useRef(null);
  useRevealOnScroll(pageRef);
  usePageMeta(PAGE_TITLE, PAGE_DESCRIPTION, CANONICAL_URL);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOMEPAGE_FAQS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <div className="hpv2" id="top" ref={pageRef}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={CANONICAL_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Manrope:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <script type="application/ld+json">{JSON.stringify(WebsiteSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(OrganizationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(getSoftwareApplicationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <V2Nav />
      <main>
        <HeroSection />
        <TrustStrip />
        <ContrastSection />
        <ProblemsSection />
        <WorkflowSection />
        <BenefitsSection />
        <AuditTrailSection />
        <TreatmentIntelligenceSection />
        <GoogleReviewsSection />
        <SecuritySection />
        <PricingSection />
        <FaqSection />
        <FinalCtaSection />
      </main>
      <FooterSection />
      <DemoLeadPopup />
      <CookieBanner />
    </div>
  );
}
